<template>
  <Form v-slot="{ errors }: FormSlot" ref="form" class="reservation-overview">
    <div class="reservation-box">
      <div class="trip-image" :style="tripImage" />

      <div class="trip-name">{{ trip.name }}</div>

      <div class="trip-date" v-html="trip.date" />

      <div class="divider" />

      <div v-if="finalTravellers && finalTravellers.length > 0">
        <div class="trip-label">Cestujúci</div>

        <table class="reservation-overview-travellers-table">
          <template
            v-for="(traveller, index) in finalTravellers"
            :key="`Overview-Traveller-index-${index}`"
          >
            <tr class="reservation-overview-traveller-item">
              <td>
                <span class="font-semi-bold"
                  >{{ traveller.first_name }} {{ traveller.last_name }}</span
                >

                <small v-if="traveller.price?.category !== 'CATEGORY_TRIP_FULL'">{{
                  traveller.price?.title
                }}</small>
              </td>

              <td class="price-cell">
                <span class="font-semi-bold">{{ traveller.price?.formatted_price }}</span>
              </td>
            </tr>

            <tr
              v-if="isPickupPlacePaid(traveller.pickup_place)"
              class="reservation-overview-service-item"
            >
              <td>
                Nástupné&nbsp;miesto:
                {{ getPickupPlace(traveller.pickup_place)?.name }}
              </td>

              <td class="price-cell">
                {{ formattedPrice(pickupPlacePrice(traveller.pickup_place)) }}
              </td>
            </tr>

            <tr
              v-for="(service, key, index) in traveller.services"
              :key="`overview-service-${key}`"
              class="reservation-overview-service-item"
              :class="{
                'reservation-overview-service-item-last':
                  index + 1 === Object.keys(traveller.services).length,
              }"
            >
              <td>
                {{ service.name }}
              </td>

              <td class="price-cell">
                {{ formattedPrice(service.price.price, service.is_billed_per_day) }}
              </td>
            </tr>
          </template>
        </table>

        <div class="divider" />
      </div>

      <VoucherList :vouchers="reservationState.vouchers" />

      <div class="total">
        <div class="trip-label">Spolu</div>

        <div class="total-price">
          {{ total }}
        </div>
      </div>

      <voucher />

      <div v-if="isMobile">
        <div>
          <div class="divider" />

          <div class="trip-label">Platobná metóda</div>

          <strong>{{ selectedPaymentMethodName }}</strong>
        </div>

        <div>
          <div class="divider" />

          <div class="trip-label">Fakturačné údaje</div>

          <strong>{{ customer.first_name }} {{ customer.last_name }}</strong>

          <div>
            <div class="text-grey-light m-t-xs">Dátum narodenia:</div>
            {{ getFormattedBirthDate(customer.birthdate) }}

            <div class="text-grey-light m-t-xs">Číslo OP:</div>
            {{ customer.id_card }}
            <br />

            <br />

            <strong>Kontaktné údaje</strong>

            <br />

            {{ customer.email }}, <br />
            {{ customer.phone }},
            <br />

            <br />

            <strong>Adresa:</strong>

            <br />

            {{ customer.address_street }}, <br />

            {{ customer.address_post_code }} {{ customer.address_city }}, <br />
            {{ addressCountry }}
          </div>
        </div>

        <div class="divider" />

        <div class="row m-b">
          <div class="col-12 col-sm-8">
            <div class="form-input">
              <label>Poznámka k objednávke</label>

              <textarea v-model="reservationState.note" rows="4" class="form-control" />
            </div>
          </div>
        </div>

        <Checkbox v-model="reservationState.newsletter_subscribe"
          >Prajem si odoberať výhodné ponuky zájazdov</Checkbox
        >

        <Field
          v-slot="{ field }: FieldSlot"
          v-model="reservationState.terms"
          name="terms"
          :rules="{ required: true }"
        >
          <Checkbox v-bind="field" name="terms">
            <div v-html="tacName" />
          </Checkbox>
        </Field>

        <div v-show="errors.terms" class="text-danger">{{ errors.terms }}</div>
      </div>
    </div>

    <ReservationBoxControls
      v-if="isMobile"
      back-button-text="Späť na platby"
      continue-button-text="Objednať s povinnosťou platby"
      @return="onReturn"
      @continue="onContinue"
    />
  </Form>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import type { FormContext } from 'vee-validate';
import { Form, Field } from 'vee-validate';
import Checkbox from './input/checkbox.vue';
import ReservationBoxControls from './ReservationBoxControls.vue';
import Voucher from './Voucher.vue';
import VoucherList from './VoucherList.vue';
import { useReservation } from '../modules/useReservation';
import { useDevice } from '../modules/useDevice';
import { pushRoute, Route } from '../router';
import { usePrice } from '../modules/usePrice';
import type { FieldSlot, FormSlot, PaymentMethodObject, Traveller } from '../types';
import { tracking } from '../modules/analytics/analytics';
import { useReservationTracking } from '../modules/useReservationTracking';

const trip = window.RESERVATION_STORE.tripBaseInfo;
const tripImage = {
  'background-image': `url(${trip.image})`,
};

const { reservationState, completeReservation } = useReservation();
const { trackPageView } = tracking();
const { trackTripEvent } = useReservationTracking();
const customer = computed(() => reservationState.value.customer);

const selectedPaymentMethod: PaymentMethodObject =
  window.RESERVATION_STORE.paymentMethods[
    reservationState.value.payment_method as keyof typeof window.RESERVATION_STORE.paymentMethods
  ];
const selectedPaymentMethodName = selectedPaymentMethod.name;

const addressCountry = window.RESERVATION_STORE.countries[customer.value.address_country];

const { getPrice, formattedPrice } = usePrice();

const { isMobile } = useDevice();

const { tacName } = window.RESERVATION_STORE;

const form = ref<FormContext | null>(null);

const onContinue = async () => {
  const response = await form.value?.validate();
  if (!response?.valid) {
    return;
  }

  trackTripEvent({ event: 'reservation_submit_reservation', value: 0 });

  completeReservation();
};

const onReturn = () => {
  pushRoute(Route.InvoiceData);
};

const getFormattedBirthDate = (birthdate: string) => {
  const birthdateSplit = birthdate.split('-');
  return `${birthdateSplit[2]}.${birthdateSplit[1]}.${birthdateSplit[0]}`;
};

const finalTravellers = computed<Traveller[]>(() =>
  Object.values(reservationState.value.travellers)
);

const isPickupPlacePaid = (pickupPlaceId: number | undefined) => {
  return pickupPlacePrice(pickupPlaceId) > 0;
};

const pickupPlacePrice = (pickupPlaceId: number | undefined) => {
  return getPickupPlace(pickupPlaceId)?.price ?? 0;
};

const getPickupPlace = (pickupPlaceId: number | undefined) => {
  return window.RESERVATION_STORE.pickupPlaces.find((item) => item.id === pickupPlaceId);
};

const total = computed(() => {
  const travellersPrice = finalTravellers.value
    .map((a) => parseFloat(String(a.price?.price)))
    .reduce((a, o) => a + o, 0);

  const servicesPrice = finalTravellers.value
    .map((a) =>
      Object.values(a.services)
        .map((a) => getPrice(parseFloat(String(a.price?.price)), a.is_billed_per_day))
        .reduce((a, o) => a + o, 0)
    )
    .reduce((a, o) => a + o, 0);

  const pickupPlacesPrice = finalTravellers.value
    .map((a) => getPrice(pickupPlacePrice(a.pickup_place)))
    .reduce((a, o) => a + o, 0);

  let total = travellersPrice + servicesPrice + pickupPlacesPrice;
  const discount = reservationState.value.vouchers
    .map((v) => parseFloat(String(v.amount)))
    .reduce((v, o) => v + o, 0);

  if (discount > total) {
    total = 0;
  } else {
    total -= discount;
  }

  return formattedPrice(total);
});

onMounted(() => {
  if (isMobile) {
    trackPageView({
      screen: 'RESERVATION_MOBILE_OVERVIEW',
    });
  }
});
</script>
